import { useState, useEffect, useRef } from 'react'
import {
	IAffiliateFilters,
	IFilterConfig,
	EFilterFetchConvertType,
	EFilterOperator,
	EFilterType,
	EFilterSpecOperator
} from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'
import { PERCENT_IS_EMPTY_OPTIONS } from '../constants'

type IUseFiltersAffiliatePage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	affiliateFiltersConfig: IFilterConfig<keyof IAffiliateFilters>[];
	defaultFiltersValue: IAffiliateFilters;
	initialFiltersValue: IAffiliateFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IAffiliateFilters | null)) => (IAffiliateFilters | null)) | IAffiliateFilters | null)) => void;
}

const AFFILIATE_FILTERS_DEFAULT: IAffiliateFilters = {
	name: '',
	'affiliate.percents.empty': null
}


export const useAffiliateFiltersPage = (): IUseFiltersAffiliatePage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)

	const profile = useAtomValue(state.profileAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IAffiliateFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const PERCENT_IS_EMPTY_OPTIONS_TRANS = PERCENT_IS_EMPTY_OPTIONS(t)

	const affiliateFiltersConfig: IFilterConfig<keyof IAffiliateFilters>[] = [
		{
			name: 'name',
			label: t(ALL_LANG_KEY.affiliate_list_table.column_name),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'affiliate.percents.empty',
			label: t(ALL_LANG_KEY.affiliate_list_table.column_percent),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: PERCENT_IS_EMPTY_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		}
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		affiliateFiltersConfig,
		AFFILIATE_FILTERS_DEFAULT
	)

	useEffect(() => {
		//console.log('hook.useAffiliateFiltersPage useEffect::START:: ', { userFilters, filter })
		if (!!userFilters) {
			setSend(true)

			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				userFilters,
				affiliateFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				return prev
			})

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			//console.log('hook.useAffiliateFiltersPage:: useEffect::END:: ', { userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		affiliateFiltersConfig,
		defaultFiltersValue: AFFILIATE_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}